import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { Tier, TierCreateInput } from "@colab/shared";

export type CreateTierInput = {
  programId: string;
  input: TierCreateInput;
};

type CreateTierResponse = { id: string; tier: Tier };

// POST /api/admin/tiers/programs/:programId/tiers
//
// Pessimistic — list-append. The synthetic-row optimistic pattern (Pattern A)
// would need a temp UUID that the server replaces on settle; matrix columns
// in the editor pin to ids, so a temp would briefly break cell-targeting.
// One round-trip and a refetch is the simpler shape here.
export function useCreateTier() {
  const queryClient = useQueryClient();
  return useMutation<CreateTierResponse, ApiClientError, CreateTierInput>({
    mutationFn: ({ programId, input }) =>
      apiFetch<CreateTierResponse>(`/admin/tiers/programs/${programId}/tiers`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { programId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(programId) });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to create tier");
    },
  });
}
